"use client";

import {
  useAddCommentMutation,
  useCommentLikesQuery,
  useCommentsQuery,
  useToggleCommentLikeMutation,
  type CommentRow,
} from "@/hooks/queries/use-comments";
import {
  useReactionCountsQuery,
  useReactionQuery,
  useSetReactionMutation,
} from "@/hooks/queries/use-reactions";
import { useViewerLanguage } from "@/lib/i18n";
import { hasCommentSection } from "@/utils/slide-kinds";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  CommentSheet,
  type CommentSheetLabels,
  type SheetComment,
} from "./comment-sheet";

const SPEAKER_TYPES = new Set(["speaker", "presenter", "host", "owner"]);

const RELATIVE_TIME_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 31536000],
  ["month", 2592000],
  ["week", 604800],
  ["day", 86400],
  ["hour", 3600],
  ["minute", 60],
];

function relativeTime(
  value: string | undefined,
  format: Intl.RelativeTimeFormat,
  now: number,
): string {
  if (!value) return "";
  const at = Date.parse(value);
  if (Number.isNaN(at)) return value;
  const seconds = Math.round((at - now) / 1000);
  for (const [unit, size] of RELATIVE_TIME_UNITS) {
    if (Math.abs(seconds) >= size) {
      return format.format(Math.trunc(seconds / size), unit);
    }
  }
  return format.format(0, "second");
}

function toSheetComment(
  c: CommentRow,
  likedIds: ReadonlySet<string>,
  formatTime: (value: string | undefined) => string,
  anonymousName: string,
): SheetComment {
  return {
    id: c.id,
    author: c.anonymous ? anonymousName : c.author,
    avatarUrl: c.avatar_url,
    time: formatTime(c.time ?? c.created_at),
    text: c.text,
    isSpeaker: SPEAKER_TYPES.has(String(c.author_type ?? "").toLowerCase()),
    likes: c.likes,
    liked: likedIds.has(c.id),
    replies: c.replies?.map((r) =>
      toSheetComment(r, likedIds, formatTime, anonymousName),
    ),
  };
}

export interface CommentsSectionProps {
  slideId: string;
  sessionId: string;
  handyoutId?: string;
  mode?: "interactive" | "editor";
  onRequireName?: (action: () => void, commentText?: string) => void;
  isInView?: boolean;
  className?: string;
  /** Overrides the totals the section fetches itself. */
  reactionCounts?: { up: number; down: number };
  labels?: Partial<CommentSheetLabels>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CommentsSection({
  slideId,
  sessionId,
  handyoutId,
  mode = "interactive",
  onRequireName,
  isInView = true,
  className,
  reactionCounts,
  labels,
  open: openProp,
  onOpenChange,
}: CommentsSectionProps) {
  const isCasualSlide = hasCommentSection(slideId);
  const { intlLocale, t } = useViewerLanguage();

  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const [commentValue, setCommentValue] = useState("");
  const [parentId, setParentId] = useState<string | undefined>(undefined);
  const currentSlideRef = useRef(slideId);
  currentSlideRef.current = slideId;

  const { data: rows = [] } = useCommentsQuery(slideId, handyoutId);
  const { data: reaction = null } = useReactionQuery(slideId, mode, handyoutId);
  const { data: reactionTotals } = useReactionCountsQuery(slideId, mode);
  const addCommentMutation = useAddCommentMutation({ slideId, parentId });
  const setReactionMutation = useSetReactionMutation(slideId);

  const disabled = !sessionId || mode !== "interactive";
  const { data: likedIds } = useCommentLikesQuery(slideId, !disabled);
  const { toggleLike, pendingLikeIds } = useToggleCommentLikeMutation(slideId);

  useEffect(() => {
    setOpen(false);
    setCommentValue("");
    setParentId(undefined);
  }, [slideId]);

  const likedSet = useMemo(() => new Set(likedIds ?? []), [likedIds]);
  const comments = useMemo(() => {
    const format = new Intl.RelativeTimeFormat(intlLocale, {
      numeric: "auto",
      style: "short",
    });
    const now = Date.now();
    const formatTime = (value: string | undefined) =>
      relativeTime(value, format, now);
    const anonymousName = t("Anonymous");
    return rows.map((r) =>
      toSheetComment(r, likedSet, formatTime, anonymousName),
    );
  }, [rows, likedSet, intlLocale, t]);
  const replyTo = parentId
    ? comments.find((c) => c.id === parentId)
    : undefined;

  const handleReact = (value: 1 | 0) => {
    if (disabled) return;
    setReactionMutation.mutate(reaction === value ? null : value);
  };

  const submitComment = (text: string, replyParentId?: string) => {
    const submittedSlideId = slideId;
    setCommentValue("");
    setParentId(undefined);
    addCommentMutation.mutateAsync({ text, parentId: replyParentId }).catch(() => {
      if (currentSlideRef.current !== submittedSlideId) return;
      setCommentValue((current) => (current.trim() ? current : text));
      setParentId((current) => current ?? replyParentId);
    });
  };

  const handleSubmit = () => {
    if (disabled) return;
    const text = commentValue.trim();
    if (!text) return;
    const replyParentId = parentId;
    const send = () => submitComment(text, replyParentId);
    if (onRequireName) onRequireName(send, text);
    else send();
  };

  if (!isCasualSlide) return null;

  return (
    <CommentSheet
      comments={comments}
      reaction={reaction}
      reactionCounts={reactionCounts ?? reactionTotals}
      onReact={handleReact}
      value={commentValue}
      onChange={setCommentValue}
      onSubmit={handleSubmit}
      replyTo={replyTo}
      onReplyChange={(c) => setParentId(c?.id)}
      onToggleLike={disabled ? undefined : toggleLike}
      pendingLikeIds={pendingLikeIds}
      open={open}
      onOpenChange={setOpen}
      disabled={disabled}
      isInView={isInView}
      labels={labels}
      className={className}
    />
  );
}
